import { Link } from 'react-router-dom';
import { Bookmark, ExternalLink, Sparkles, Eye, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { RatingStars } from '@/components/community/RatingStars';
import { useFavorites } from '@/hooks/useFavorites';
import { useCommentCount } from '@/hooks/community/useCommentCount';
import { cn } from '@/lib/utils';
import { Tool } from '@/hooks/useTools';

interface ToolCardProps {
  tool: Tool;
  className?: string;
  aiExplanation?: {
    reason: string;
    relevanceScore: number;
  };
}

const platformIcons: Record<string, string> = {
  'Web': '🌐',
  'macOS': '🍎',
  'Windows': '🪟',
  'iOS': '📱',
  'Android': '🤖',
  'Plugins': '🔌',
};

const getLogoUrl = (tool: Tool): string => {
  // Priority 1: Check if we have a stored logo in Supabase Storage
  if (tool.logo && !tool.logo.startsWith('http')) {
    return `https://oslzwjyyhugvrzrnwhuc.supabase.co/storage/v1/object/public/tool-logos/${tool.logo}`;
  }
  
  // Priority 2: Use the tool's existing logo URL if it's a full URL
  if (tool.logo && tool.logo.startsWith('http')) {
    return tool.logo;
  }
  
  // Priority 3: Try Clearbit logo API if we have a URL
  if (tool.url) {
    try {
      const domain = new URL(tool.url).hostname;
      return `https://logo.clearbit.com/${domain}`;
    } catch {
      // Fall through to local logos
    }
  }
  
  // Priority 4: Fallback to local logos
  return `/logos/${tool.slug}.svg`;
};

const pricingColors: Record<string, string> = {
  'Free': 'bg-accent-light text-accent',
  'Freemium': 'bg-accent-light text-accent',
  'Paid': 'bg-accent-light text-accent',
};

export function ToolCard({ tool, className, aiExplanation }: ToolCardProps) {
  const { favorites, toggleFavorite } = useFavorites();
  const { count: commentCount } = useCommentCount('tool', tool.id);
  const isFavorite = favorites.includes(tool.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(tool.id);
  };

  const handleExternalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(tool.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="group hover:shadow-lg hover:shadow-accent/10 transition-all duration-300 hover:scale-[1.02] h-full flex flex-col border-border/50 hover:border-accent/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Tool Logo */}
            <div className="h-12 w-12 rounded-lg bg-surface flex items-center justify-center overflow-hidden">
              <img 
                src={getLogoUrl(tool)}
                alt={`${tool.name} logo`}
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  // Try fallback sources in order
                  const target = e.target as HTMLImageElement;
                  const currentSrc = target.src;
                  
                  // Try Clearbit as first fallback
                  if (tool.url && !currentSrc.includes('logo.clearbit.com')) {
                    const domain = new URL(tool.url).hostname;
                    target.src = `https://logo.clearbit.com/${domain}`;
                    return;
                  }
                  
                  // Try local logos as second fallback
                  if (!currentSrc.includes('/logos/')) {
                    target.src = `/logos/${tool.slug}.svg`;
                    return;
                  }
                  
                  // Final fallback to letter
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `<span class="text-lg font-semibold text-text-primary">${tool.name.charAt(0)}</span>`;
                  }
                }}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-heading font-semibold text-lg text-text-primary group-hover:text-primary transition-colors duration-200 truncate">
                  {tool.name}
                </h3>
                {tool.ai_enabled && (
                  <Badge variant="secondary" className="bg-accent-light text-accent text-xs px-2 py-0.5 flex items-center space-x-1">
                    <Sparkles className="h-3 w-3" />
                    <span>AI</span>
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge 
                  variant="secondary" 
                  className={cn(
                    "text-xs",
                    pricingColors[tool.pricing] || "bg-surface text-text-secondary"
                  )}
                >
                  {tool.pricing}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <p className="text-sm text-text-secondary line-clamp-3 mb-4 flex-1">
          {tool.summary}
        </p>

        {/* Categories */}
        {tool.category && tool.category.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {tool.category.slice(0, 3).map((category) => (
                 <Badge 
                   key={category}
                   variant="outline" 
                   className="text-xs px-2 py-1 truncate"
                 >
                   {category}
                 </Badge>
              ))}
              {tool.category.length > 3 && (
                <Badge variant="outline" className="text-xs px-2 py-1 text-text-tertiary">
                  +{tool.category.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Use Cases */}
        {tool.use_cases && tool.use_cases.length > 0 && (
          <div className="mb-4">
            <span className="text-xs text-text-tertiary mb-1 block">Use Cases:</span>
            <div className="flex flex-wrap gap-1">
              {tool.use_cases.slice(0, 2).map((useCase) => (
                 <Badge 
                   key={useCase}
                   variant="accent" 
                   className="text-xs px-2 py-1 truncate"
                 >
                   {useCase}
                 </Badge>
              ))}
              {tool.use_cases.length > 2 && (
                <Badge variant="accent" className="text-xs px-2 py-1 text-text-tertiary">
                  +{tool.use_cases.length - 2}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Community Features */}
        <div className="mb-3 flex items-center justify-between">
          <RatingStars entityType="tool" entityId={tool.id} compact={true} />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFavoriteClick}
              className={cn(
                "h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-all duration-base",
                isFavorite && "opacity-100 text-primary"
              )}
            >
              <Bookmark className={cn("h-3 w-3", isFavorite && "fill-current")} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-all duration-base relative"
            >
              <MessageCircle className="h-3 w-3" />
              {commentCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                  {commentCount > 99 ? '99+' : commentCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* AI Explanation */}
        {aiExplanation && (
          <div className="mb-4 p-3 bg-gradient-to-r from-accent-light/10 to-accent-light/5 border border-accent/20 rounded-lg animate-scale-in">
            <div className="flex items-start space-x-2">
              <Sparkles className="h-4 w-4 text-accent flex-shrink-0 mt-0.5 animate-pulse" />
              <div className="flex-1">
                <p className="text-sm text-text-secondary leading-relaxed">
                  {aiExplanation.reason}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="outline" className="text-xs bg-accent-light/20 text-accent border-accent/30">
                    {aiExplanation.relevanceScore}% Match
                  </Badge>
                  <span className="text-xs text-text-tertiary">AI Recommended</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-auto pt-4">
          <Button 
            asChild
            variant="outline" 
            size="sm" 
            className="flex-1"
          >
            <Link to={`/tool/${tool.slug}`}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </Link>
          </Button>
          {tool.url && (
            <Button 
              onClick={handleExternalClick}
              variant="outline"
              size="sm" 
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}